import { jsx, jsxs } from 'react/jsx-runtime';
import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useLockBodyScroll } from 'react-use';
import '../../assets/icons/fallbackImage.svg.mjs';
import '../../assets/icons/zoom-in.svg.mjs';
import '../../constants/icons/generic/close.svg.mjs';
import { DEFAULT_MOTIF } from '../../constants/theme.mjs';
import '../StyleableSvgImage/index.mjs';
import { ImageWrapOuter, ImageWrap, FallbackImageWrap, ImageSkeletonLoader, FullscreenImageWrap, FullscreenCloseButton, FullscreenButton, ZoomIcon } from './CustomImage.styles.mjs';
import FallbackImageIcon__default from "../../assets/fallbackImage.svg";
import { StyleableSvgImage } from '../StyleableSvgImage/StyleableSvgImage.mjs';
import CloseIcon__default from "../../assets/close2.svg";
import ZoomInIcon__default from "../../assets/zoom-in.svg";
"use strict";
const isNextImageLoading = imageEl => {
  return (
    // Loading if no src found
    !imageEl.src ||
    // Loading if next placeholder is found
    /** @see https://github.com/vercel/next.js/issues/18398#issuecomment-850251992 */
    imageEl.src.includes("data:image/gif;base64") ||
    // Loading if somehow the replaced actual image is not completely loaded
    !imageEl.complete
  );
};
const asset = (asset2, width, height) => ({
  src: typeof asset2 === "string" ? asset2 : asset2.src,
  width,
  height
});
const renderNativeImage = ({
  src,
  height,
  width,
  alt,
  onLoad,
  onError
}) => /* @__PURE__ */jsx("img", {
  src,
  height,
  width,
  alt: alt ?? "",
  onLoad,
  onError
});
const CustomImage = function CustomImage2({
  motif = DEFAULT_MOTIF,
  asset: asset2,
  sizes,
  className,
  margin,
  priority = false,
  fallbackImageSize = "medium",
  responsive,
  layout = "responsive",
  renderImage,
  disableSkeleton,
  objectFit,
  disableBorderRadius,
  canFullscreen,
  onFullscreenChange
}) {
  const [loading, setLoading] = useState(
  // Set as loading by default, handle not loading in the effect
  true);
  const [error, setError] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  useLockBodyScroll(isFullscreen);
  useEffect(() => {
    if (canFullscreen && onFullscreenChange) {
      onFullscreenChange(isFullscreen);
    }
  }, [canFullscreen, isFullscreen, onFullscreenChange]);
  const wrapRef = useRef(null);
  useEffect(() => {
    if (!wrapRef.current) return;
    const imageEl = wrapRef.current.querySelector("img");
    if (!imageEl) return;
    const loadingInitial = isNextImageLoading(imageEl);
    setLoading(loadingInitial);
    setError(false);
  }, []);
  const handleOnError = useCallback(() => {
    setError(true);
  }, []);
  const handleOnLoadingComplete = useCallback(() => {
    setLoading(false);
    setError(false);
  }, []);
  const handleOnLoadComplete = useCallback(event => {
    if (!event?.currentTarget?.src || event.currentTarget.src === asset2?.src) {
      setLoading(false);
      setError(false);
    }
  }, [asset2?.src]);
  const handleFullscreenClick = useCallback(() => {
    if (canFullscreen && asset2 && !error && !loading && !isFullscreen) {
      setIsFullscreen(true);
    }
  }, [asset2, canFullscreen, error, isFullscreen, loading]);
  useEffect(() => {
    if (!canFullscreen) {
      return;
    }
    const handleKeyDown = e => {
      if (e.key === "Escape") {
        setIsFullscreen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [canFullscreen]);
  return /* @__PURE__ */jsxs(ImageWrapOuter, {
    className,
    $responsive: responsive,
    children: [/* @__PURE__ */jsx(ImageWrap, {
      margin,
      layout,
      $sizes: sizes,
      $objectFit: objectFit,
      ref: wrapRef,
      $clickable: canFullscreen && asset2 && !error && !loading && !isFullscreen,
      onClick: handleFullscreenClick,
      children: asset2 && !error ? renderImage?.({
        src: asset2.src,
        alt: asset2.alt,
        // Don't pass width and height to next/image if layout is "fill"
        height: layout !== "fill" ? asset2.height : void 0,
        width: layout !== "fill" ? asset2.width : void 0,
        onLoadingComplete: handleOnLoadingComplete,
        onLoad: handleOnLoadComplete,
        onError: handleOnError,
        sizes,
        layout,
        priority,
        objectFit
      }) ?? renderNativeImage({
        ...asset2,
        onLoad: handleOnLoadingComplete,
        onError: handleOnError
      }) : /* @__PURE__ */jsx(FallbackImageWrap, {
        $margin: margin,
        $fallbackImageSize: fallbackImageSize,
        children: /* @__PURE__ */jsx("img", {
          src: typeof FallbackImageIcon__default === "string" ? FallbackImageIcon__default : FallbackImageIcon__default.src,
          alt: "Fallback"
        })
      })
    }), !disableSkeleton && loading && asset2 && !error && /* @__PURE__ */jsx(ImageSkeletonLoader, {
      $responsive: responsive,
      $motif: motif,
      $disableBorderRadius: disableBorderRadius
    }), canFullscreen && isFullscreen && asset2 && !error && createPortal(/* @__PURE__ */jsxs(FullscreenImageWrap, {
      children: [renderImage?.({
        src: asset2.src,
        alt: asset2.alt,
        height: layout !== "fill" ? asset2.height : void 0,
        width: layout !== "fill" ? asset2.width : void 0,
        sizes,
        layout,
        priority,
        objectFit
      }) ?? renderNativeImage(asset2), /* @__PURE__ */jsx(FullscreenCloseButton, {
        onClick: () => setIsFullscreen(false),
        children: /* @__PURE__ */jsx(StyleableSvgImage, {
          src: CloseIcon__default,
          width: 14,
          height: 14
        })
      })]
    }), document.body), canFullscreen && asset2 && !error && !loading && !isFullscreen && /* @__PURE__ */jsx(FullscreenButton, {
      onClick: handleFullscreenClick,
      children: /* @__PURE__ */jsx(ZoomIcon, {
        src: ZoomInIcon__default,
        width: 32,
        height: 32
      })
    })]
  });
};
export { CustomImage, asset, renderNativeImage };
//# sourceMappingURL=CustomImage.mjs.map
